import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { mensajeDe } from "../utiles.ts";
import { Boton, Enlace } from "./ui.tsx";

/**
 * Un PDF que imprime el worker (la vista previa de una revisión, una hoja con su tinta). Si ya hay
 * uno, se abre enseguida; si no, se pide y se pregunta cada 3 s hasta que llegue, el trabajo falle
 * o pasen dos minutos. Si lo que se ve cambia mientras tanto (`version`), ese PDF ya no sirve y se
 * pide otro.
 */

/** Lo que dice la API de un PDF pedido: ya está, o el worker no pudo. */
type EstadoDelPdf = { archivoId: string | null; fallo: { motivo: string } | null };

type Opciones = {
  /** Identifica el PDF en la caché de consultas (la hoja o la revisión). */
  clave: readonly unknown[];
  /** Pregunta si el PDF ya está; se repite cada 3 s mientras se espera. */
  consultar: () => Promise<EstadoDelPdf>;
  /** Pide el PDF a la API: ya listo (de lo guardado), o encargado al worker. */
  pedir: () => Promise<{ lista: boolean; archivoId: string | null }>;
  /** Guarda lo pendiente antes de pedir, para que el PDF sea de lo que se ve. */
  antes?: (() => Promise<void>) | undefined;
  onAviso: (mensaje: string | null) => void;
  /** Un PDF que ya existía al abrir la pantalla. */
  inicial?: string | null;
  /** Resume lo que se ve: si cambia, el PDF que había (hecho o en camino) quedó viejo. */
  version?: string | undefined;
};

function usePdfEnCamino({
  clave,
  consultar,
  pedir,
  antes,
  onAviso,
  inicial = null,
  version,
}: Opciones) {
  const [esperando, setEsperando] = useState(false);
  const [archivoId, setArchivoId] = useState<string | null>(inicial);
  const pedidaCon = useRef(version);
  // Mientras se pide, el guardado de `antes` también cambia la versión, y eso no cuenta.
  const pidiendo = useRef(false);
  const pedido = useMutation({ mutationFn: pedir });
  const estado = useQuery({
    queryKey: ["pdf-en-camino", ...clave],
    queryFn: consultar,
    enabled: esperando,
    refetchInterval: esperando ? 3000 : false,
    // Cada espera empieza preguntando de nuevo, no con lo que quedó de la anterior.
    staleTime: 0,
  });
  const listo = estado.data?.archivoId ?? null;
  const fallo = estado.data?.fallo ?? null;

  useEffect(() => {
    if (esperando && listo) {
      setArchivoId(listo);
      setEsperando(false);
    }
  }, [esperando, listo]);

  // El worker lo intentó y no pudo: no tiene sentido seguir preguntando cada tres segundos.
  useEffect(() => {
    if (!esperando || !fallo) return;
    setEsperando(false);
    onAviso(`No se pudo generar el PDF: ${fallo.motivo}. Prueba de nuevo más tarde.`);
  }, [esperando, fallo, onAviso]);

  // Si en dos minutos no llegó, algo pasa con el worker: se deja de preguntar y se dice.
  useEffect(() => {
    if (!esperando) return;
    const t = setTimeout(() => {
      setEsperando(false);
      onAviso("El PDF está tardando más de lo normal. Prueba de nuevo en un momento.");
    }, 120_000);
    return () => clearTimeout(t);
  }, [esperando, onAviso]);

  async function pedirAhora() {
    onAviso(null);
    setArchivoId(null);
    pidiendo.current = true;
    try {
      await antes?.();
      const r = await pedido.mutateAsync();
      if (r.lista && r.archivoId) setArchivoId(r.archivoId);
      else setEsperando(true);
    } catch (e) {
      onAviso(mensajeDe(e));
    } finally {
      pidiendo.current = false;
    }
  }

  // Se escribió después de pedirlo: el PDF (hecho o en camino) es de antes.
  useEffect(() => {
    if (version === pedidaCon.current) return;
    pedidaCon.current = version;
    if (pidiendo.current) return;
    setArchivoId(null);
    setEsperando(false);
  }, [version]);

  return { archivoId, esperando, pidiendo: pedido.isPending, pedirAhora };
}

/** El botón que pide el PDF y, cuando está, el enlace para abrirlo en otra pestaña. */
export function BotonPdf({ etiqueta, ...opciones }: Opciones & { etiqueta: string }) {
  const pdf = usePdfEnCamino(opciones);
  return (
    <>
      <Boton
        variante="secundario"
        disabled={pdf.esperando || pdf.pidiendo}
        onClick={() => void pdf.pedirAhora()}
      >
        {pdf.esperando ? "Generando PDF…" : etiqueta}
      </Boton>
      {pdf.archivoId && (
        <Enlace
          variante="primario"
          href={`/archivos/${pdf.archivoId}?ver=1`}
          target="_blank"
          rel="noreferrer"
        >
          Abrir PDF
        </Enlace>
      )}
    </>
  );
}
